/**
 * The status chips on a Candidate Requests card: what kind of draft it carries, whether it has
 * already been answered, and which question category it was classified as.
 *
 * Split out of InboundCard purely for that file's 500-line ceiling. The two style helpers come with
 * it because they exist only for these chips — `categoryStyle` encodes the rule that a draft is only
 * shown as "ready" (blue) when it actually answers the question.
 */
import { Paperclip } from 'lucide-react';
import type { InboundRequest } from '../../types/taInboundRequests';
import { COLORS } from './format';
import { acknowledgesOnly } from './requestGroups';

/** True when the draft acknowledges without answering — the recruiter adds the substance. */
// The badge, the card copy and the queue buckets share ONE definition — see requestGroups.ts.
export { acknowledgesOnly };

/** Blue when a reply is ready to review, amber when a human has to write it, gray when idle. */
function categoryStyle(item: InboundRequest) {
  // A closing thanks is not work: calm gray, not call-to-action amber.
  if (item.draftSource === 'no_reply') {
    return { backgroundColor: '#F4F4F5', color: '#52525B' };
  }
  /**
   * A cold-lane card is NEVER "ready". Lever's lookup is one exact address, so
   * this could be a real candidate writing from a second account — and the
   * prepared reply says "we cannot consider applications sent by email", which
   * would be badly wrong for them. Amber keeps the recruiter's eyes on it.
   */
  if (!item.leverMatched) {
    return { backgroundColor: COLORS.orangeBg, color: COLORS.orangeText };
  }
  /**
   * A FEEDBACK draft is never "ready" either. It is built from interviewers' notes and goes to a
   * rejected candidate: AGG-checked, but the recruiter reads it before it leaves. Amber keeps it in
   * front of them.
   */
  if (item.draftSource === 'feedback') {
    return { backgroundColor: COLORS.orangeBg, color: COLORS.orangeText };
  }
  // Blue only when the draft actually answers; holding and generic replies are not "done".
  return item.draft && !acknowledgesOnly(item)
    ? { backgroundColor: COLORS.blueBg, color: COLORS.blueText }
    : { backgroundColor: COLORS.orangeBg, color: COLORS.orangeText };
}

export function CardBadges({ item }: { item: InboundRequest }) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      {(item.attachmentCount ?? 0) > 0 && (
        <span
          className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
          title="The thread carries attachments — expand the card to open them"
        >
          <Paperclip className="mr-1 inline h-3 w-3" />
          {item.attachmentCount}
        </span>
      )}
      {item.draftSource === 'template' && (
        <span
          className="rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ backgroundColor: '#EDE9FE', color: '#5B21B6' }}
          title="Follows FINN's approved reply for this kind of message, written for this one"
        >
          standard reply
        </span>
      )}
      {acknowledgesOnly(item) && (
        <span
          className="rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
          title="Prepared, but it only acknowledges — you add the answer"
        >
          needs your answer
        </span>
      )}
      {item.draftSource === 'no_reply' && (
        <span
          className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600"
          title="The thread ends in a closing thanks — probably nothing to do"
        >
          no reply needed
        </span>
      )}
      {(item.answeredAt || item.draftedAt) && (
        <span
          className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700"
          title={
            item.answeredAt
              ? 'A reply has already gone out from this tab. It reappeared because the candidate wrote again.'
              : 'A Gmail draft was created for this thread by the older draft flow.'
          }
        >
          {item.answeredAt ? 'replied' : 'draft in Gmail'}
        </span>
      )}
      <span
        className="rounded-full px-2.5 py-1 text-xs font-semibold"
        style={categoryStyle(item)}
      >
        {item.categoryLabel}
      </span>
    </div>
  );
}
